import { AvatarDisplay } from "@/components/AvatarDisplay";
import { TierFrameAvatar } from "@/components/TierFrameAvatar";
import type { LeaderboardEntry } from "@/lib/domain/leaderboard";
import { useActiveEventMode } from "@/lib/hooks/useActiveEventMode";
import { useTierLabel } from "@/hooks/useTierLabel";

interface LeaderboardPodiumProps {
  topThree: LeaderboardEntry[];
  onEntryClick?: (userId: string) => void;
  /**
   * Force the event (Betsson/World Cup) styling regardless of region. The
   * landing page uses this so the leaderboard matches the rest of its event UI
   * (which is gated on `eventEnabled`, shown to everyone while the flag is on),
   * whereas in-app surfaces leave it undefined and fall back to the
   * region-gated `isEventMode`.
   */
  eventMode?: boolean;
  /** Optional title above each podium bar (e.g. "No.1 fan" on a club board). */
  renderBadge?: (rank: PodiumRank) => React.ReactNode;
}

const poppins = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 600,
  letterSpacing: "0",
  lineHeight: 1,
} as const;

export type PodiumRank = 1 | 2 | 3;

const eventPodiumConfig: Record<
  PodiumRank,
  {
    barHeight: string;
    ringColor: string;
    badgeColor: string;
    gradientFrom: string;
    gradientTo: string;
    order: string;
  }
> = {
  1: {
    barHeight: "h-36 sm:h-48",
    ringColor: "#FFD700",
    badgeColor: "#FFD700",
    gradientFrom: "rgba(255,215,0,0.85)",
    gradientTo: "rgba(255,176,0,0.35)",
    order: "order-2",
  },
  2: {
    barHeight: "h-28 sm:h-36",
    ringColor: "#C0C0C0",
    badgeColor: "#C0C0C0",
    gradientFrom: "rgba(214,214,222,0.8)",
    gradientTo: "rgba(160,160,170,0.3)",
    order: "order-1",
  },
  3: {
    barHeight: "h-20 sm:h-28",
    ringColor: "#CD7F32",
    badgeColor: "#CD7F32",
    gradientFrom: "rgba(205,127,50,0.85)",
    gradientTo: "rgba(160,90,30,0.35)",
    order: "order-3",
  },
};

const podiumConfig: Record<
  PodiumRank,
  {
    height: string;
    bg: string;
    nameColor: string;
    rpColor: string;
    order: string;
  }
> = {
  1: {
    height: "h-40 sm:h-52",
    bg: "#FFD700",
    nameColor: "text-black",
    rpColor: "text-black",
    order: "order-2",
  },
  2: {
    height: "h-32 sm:h-40",
    bg: "#C7CBD1",
    nameColor: "text-black",
    rpColor: "text-black",
    order: "order-1",
  },
  3: {
    height: "h-24 sm:h-32",
    bg: "#CD7F32",
    nameColor: "text-black",
    rpColor: "text-black",
    order: "order-3",
  },
};

export function LeaderboardPodium({ topThree, onEntryClick, eventMode, renderBadge }: LeaderboardPodiumProps) {
  const { isEventMode: regionEventMode } = useActiveEventMode();
  const tierLabelOf = useTierLabel();
  const isEventMode = eventMode ?? regionEventMode;

  const [first, second, third] = [
    topThree.find((p) => p.rank === 1),
    topThree.find((p) => p.rank === 2),
    topThree.find((p) => p.rank === 3),
  ];

  if (!first) return null;

  const players: { entry: LeaderboardEntry | undefined; rank: PodiumRank }[] = [
    { entry: second, rank: 2 },
    { entry: first, rank: 1 },
    { entry: third, rank: 3 },
  ];

  if (!isEventMode) {
    return (
      <div className="overflow-visible px-4 pt-10 sm:px-6 sm:pt-14">
        <div className="flex items-end justify-center gap-2 sm:gap-4 w-full max-w-md mx-auto">
          {players.map(({ entry, rank }) => {
            const config = podiumConfig[rank];

            if (!entry) {
              return <div key={rank} className={`flex-1 ${config.order}`} />;
            }

            const interactive = !!onEntryClick;

            return (
              <div
                key={entry.id}
                onClick={interactive ? () => onEntryClick(entry.id) : undefined}
                onKeyDown={
                  interactive
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onEntryClick(entry.id);
                        }
                      }
                    : undefined
                }
                role={interactive ? "button" : undefined}
                tabIndex={interactive ? 0 : undefined}
                className={`flex min-w-0 flex-1 flex-col items-center ${config.order} ${
                  interactive ? "cursor-pointer active:translate-y-[2px] transition-transform" : ""
                }`}
              >
                {/* Avatar — floats well above the top of the bar */}
                <div className="relative mb-3 sm:mb-4 z-10 shrink-0">
                  <div className="hidden sm:block">
                    <AvatarDisplay
                      customization={entry.avatarCustomization ?? { base: entry.avatar || `avatar-${rank}` }}
                      size="md"
                      countryCode={entry.country}
                    />
                  </div>
                  <div className="block sm:hidden">
                    <AvatarDisplay
                      customization={entry.avatarCustomization ?? { base: entry.avatar || `avatar-${rank}` }}
                      size="sm"
                      countryCode={entry.country}
                    />
                  </div>
                </div>

                {renderBadge && <div className="mb-2 flex justify-center">{renderBadge(rank)}</div>}

                {/* Bar */}
                <div
                  className={`relative w-full ${config.height} flex flex-col items-center justify-center gap-2 rounded-[10px] px-2 sm:px-3 overflow-hidden`}
                  style={{ backgroundColor: config.bg }}
                >
                  <span
                    className={`w-full text-[10px] sm:text-xs uppercase ${config.nameColor} text-center font-fun font-black leading-tight break-all line-clamp-2`}
                    title={entry.username}
                  >
                    {entry.username}
                  </span>
                  <span
                    className={`text-lg sm:text-2xl tabular-nums ${config.rpColor}`}
                    style={poppins}
                  >
                    {entry.rankPoints.toLocaleString()}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-visible px-4 pt-6 sm:px-6 sm:pt-8">
      <div className="flex items-end justify-center gap-3 sm:gap-5 w-full max-w-lg mx-auto">
        {players.map(({ entry, rank }) => {
          const config = eventPodiumConfig[rank];

          if (!entry) {
            return <div key={rank} className={`flex-1 ${config.order}`} />;
          }

          const interactive = !!onEntryClick;

          return (
            <div
              key={entry.id}
              onClick={interactive ? () => onEntryClick(entry.id) : undefined}
              onKeyDown={
                interactive
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onEntryClick(entry.id);
                      }
                    }
                  : undefined
              }
              role={interactive ? "button" : undefined}
              tabIndex={interactive ? 0 : undefined}
              className={`flex min-w-0 flex-1 flex-col items-center ${config.order} ${
                interactive ? "cursor-pointer active:translate-y-[2px] transition-transform" : ""
              }`}
            >
              {/* Avatar inside tier card frame */}
              <div className="relative mb-2 sm:mb-3 z-10 shrink-0">
                <div className="hidden sm:block">
                  <TierFrameAvatar
                    tier={entry.tier}
                    frameAlt={tierLabelOf(entry.tier)}
                    avatarCustomization={entry.avatarCustomization}
                    avatarFallback={entry.avatar || `avatar-${rank}`}
                    countryCode={entry.country}
                    size="lg"
                  />
                </div>
                <div className="block sm:hidden">
                  <TierFrameAvatar
                    tier={entry.tier}
                    frameAlt={tierLabelOf(entry.tier)}
                    avatarCustomization={entry.avatarCustomization}
                    avatarFallback={entry.avatar || `avatar-${rank}`}
                    countryCode={entry.country}
                    size="md"
                  />
                </div>
                {/* Rank badge */}
                <div
                  className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex size-7 sm:size-8 items-center justify-center rounded-full text-sm sm:text-base font-black text-white shadow-lg z-20"
                  style={{ backgroundColor: config.badgeColor }}
                >
                  {rank}
                </div>
              </div>

              {renderBadge && <div className="mb-2 flex justify-center">{renderBadge(rank)}</div>}

              {/* Gradient bar with dots */}
              <div
                className={`relative w-full ${config.barHeight} flex flex-col items-center justify-center gap-1.5 rounded-t-[10px] overflow-hidden`}
                style={{ background: `linear-gradient(180deg, ${config.gradientFrom} 0%, ${config.gradientTo} 100%)` }}
              >
                <span
                  className="w-full text-[10px] sm:text-xs uppercase text-white text-center font-fun font-black leading-tight break-all line-clamp-2"
                  title={entry.username}
                >
                  {entry.username}
                </span>
                <span
                  className="text-lg sm:text-2xl tabular-nums text-white"
                  style={poppins}
                >
                  {entry.rankPoints.toLocaleString()}
                </span>

              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
